import { Suspense, useMemo, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { graphql, useLazyLoadQuery, usePaginationFragment, useMutation } from 'react-relay';
import { usePlayer, type QueuedEpisode } from '@/lib/PlayerContext';

// SVG icon for autoplay toggle
function AutoplayIcon({ enabled, className = "h-5 w-5" }: { enabled: boolean; className?: string }) {
  return (
    <svg
      className={className}
      fill={enabled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={enabled ? 0 : 2}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      {/* Repeat/continuous play icon */}
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );
}
import { formatDuration, formatDate } from '@/utils/format';
import { QueryErrorBoundary } from '@/components/QueryErrorBoundary';
import type { DashboardHomeQuery } from '@/__generated__/DashboardHomeQuery.graphql';
import type { DashboardHome_episodes$key } from '@/__generated__/DashboardHome_episodes.graphql';
import type { DashboardHomeLikeMutation } from '@/__generated__/DashboardHomeLikeMutation.graphql';
import type { DashboardHomeUnlikeMutation } from '@/__generated__/DashboardHomeUnlikeMutation.graphql';

/**
 * Root query for DashboardHome - fetches new episodes from followed topics
 */
const dashboardHomeQuery = graphql`
  query DashboardHomeQuery {
    ...DashboardHome_episodes
  }
`;

/**
 * Fragment for paginated episodes from followed topics
 */
const DashboardHome_episodes = graphql`
  fragment DashboardHome_episodes on QueryRoot
  @refetchable(queryName: "DashboardHomeRefetchQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 20 }
    after: { type: "String" }
  ) {
    newEpisodesFromFollowed(first: $first, after: $after)
      @connection(key: "DashboardHome_newEpisodesFromFollowed") {
      edges {
        node {
          id
          topicSlug
          episodeId
          title
          summary
          audioS3Key
          durationSeconds
          publishedAt
          isLikedByViewer
          likeCount
          viewerListenProgress {
            progressSeconds
            durationSeconds
            completed
            listenedAt
          }
          chapters {
            id
            title
            startTimeMs
            endTimeMs
            description
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

/**
 * Mutation to like an episode
 */
const likeEpisodeMutation = graphql`
  mutation DashboardHomeLikeMutation($input: LikeEpisodeInput!) {
    likeEpisode(input: $input) {
      episode {
        id
        isLikedByViewer
        likeCount
      }
      userErrors {
        message
        field
      }
    }
  }
`;

/**
 * Mutation to unlike an episode
 */
const unlikeEpisodeMutation = graphql`
  mutation DashboardHomeUnlikeMutation($input: UnlikeEpisodeInput!) {
    unlikeEpisode(input: $input) {
      episode {
        id
        isLikedByViewer
        likeCount
      }
      userErrors {
        message
        field
      }
    }
  }
`;

interface EpisodeData {
  readonly id: string;
  readonly topicSlug: string;
  readonly episodeId: string;
  readonly title: string;
  readonly summary: string;
  readonly audioS3Key: string;
  readonly durationSeconds: number;
  readonly publishedAt: string;
  readonly isLikedByViewer: boolean | null | undefined;
  readonly likeCount: number;
  readonly viewerListenProgress: {
    readonly progressSeconds: number;
    readonly durationSeconds: number;
    readonly completed: boolean;
    readonly listenedAt: string;
  } | null | undefined;
  readonly chapters: ReadonlyArray<{
    readonly id: string;
    readonly title: string;
    readonly startTimeMs: number;
    readonly endTimeMs: number;
    readonly description: string | null | undefined;
  }>;
}

interface EpisodeFeedCardProps {
  episode: EpisodeData;
  onPlay: () => void;
  onLike: () => void;
  onUnlike: () => void;
  isCurrentEpisode: boolean;
  isPlaying: boolean;
  isLikeMutationPending: boolean;
}

function EpisodeFeedCard({ episode, onPlay, onLike, onUnlike, isCurrentEpisode, isPlaying, isLikeMutationPending }: EpisodeFeedCardProps) {
  const progressPercent = episode.viewerListenProgress
    ? Math.round((episode.viewerListenProgress.progressSeconds / episode.viewerListenProgress.durationSeconds) * 100)
    : 0;

  const isLiked = episode.isLikedByViewer === true;

  const handleLikeClick = () => {
    if (isLiked) {
      onUnlike();
    } else {
      onLike();
    }
  };

  return (
    <div
      className={`rounded-xl border bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-4 transition-all hover:border-gray-700 ${
        isCurrentEpisode ? 'border-indigo-500 ring-1 ring-indigo-500/50' : 'border-gray-800'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Play button */}
        <button
          type="button"
          onClick={onPlay}
          className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
            isPlaying
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg className="h-5 w-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        {/* Episode info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <Link
              to={`/p/${episode.topicSlug}`}
              className="font-medium text-indigo-400 hover:text-indigo-300"
            >
              {episode.topicSlug}
            </Link>
            <span>•</span>
            <span>{formatDate(episode.publishedAt)}</span>
          </div>

          <h3 className="font-semibold text-white line-clamp-2 mb-1">
            {episode.title}
          </h3>

          <p className="text-sm text-gray-400 line-clamp-2 mb-3">
            {episode.summary}
          </p>

          {/* Progress bar (if started) */}
          {episode.viewerListenProgress && progressPercent > 0 && (
            <div className="mb-2">
              <div className="h-1 w-full rounded-full bg-gray-700">
                <div
                  className={`h-1 rounded-full ${
                    episode.viewerListenProgress.completed ? 'bg-green-500' : 'bg-indigo-500'
                  }`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                <span>
                  {episode.viewerListenProgress.completed
                    ? 'Completed'
                    : `${formatDuration(episode.viewerListenProgress.progressSeconds)} played`}
                </span>
                <span>{formatDuration(episode.durationSeconds)}</span>
              </div>
            </div>
          )}

          {/* Meta info and like button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {!episode.viewerListenProgress && (
                <span>{formatDuration(episode.durationSeconds)}</span>
              )}
            </div>

            {/* Like button */}
            <button
              type="button"
              onClick={handleLikeClick}
              disabled={isLikeMutationPending}
              className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isLiked
                  ? 'text-red-400 hover:text-red-300'
                  : 'text-gray-400 hover:text-red-400'
              }`}
              aria-label={isLiked ? 'Unlike episode' : 'Like episode'}
            >
              {isLikeMutationPending ? (
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <svg
                  className="h-4 w-4"
                  fill={isLiked ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth={isLiked ? 0 : 2}
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"
                  />
                </svg>
              )}
              {episode.likeCount > 0 && <span>{episode.likeCount}</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-800">
        <svg
          className="h-8 w-8 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">No new episodes</h3>
      <p className="text-gray-400 mb-6 max-w-md mx-auto">
        Follow some topics to see new episodes here. Your personalized feed will show the latest content from topics you follow.
      </p>
      <Link
        to="/dashboard/discover"
        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-500"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
        </svg>
        Discover Topics
      </Link>
    </div>
  );
}

interface DashboardHomeContentProps {
  queryRef: DashboardHome_episodes$key;
}

function DashboardHomeContent({ queryRef }: DashboardHomeContentProps) {
  const { playEpisode, currentEpisode, isPlaying, setIsPlaying, setEpisodeQueue, autoplayEnabled, setAutoplayEnabled } = usePlayer();
  const [pendingLikeEpisodeId, setPendingLikeEpisodeId] = useState<string | null>(null);

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    DashboardHome_episodes,
    queryRef
  );

  const [commitLike] = useMutation<DashboardHomeLikeMutation>(likeEpisodeMutation);
  const [commitUnlike] = useMutation<DashboardHomeUnlikeMutation>(unlikeEpisodeMutation);

  const episodes = useMemo(() => {
    return data.newEpisodesFromFollowed.edges.map((edge) => edge.node);
  }, [data.newEpisodesFromFollowed.edges]);

  // Build the episode queue for autoplay - includes completion status for each episode
  const buildEpisodeQueue = useCallback((allEpisodes: readonly EpisodeData[]): QueuedEpisode[] => {
    return allEpisodes.map((ep) => ({
      episodeId: ep.episodeId,
      topicSlug: ep.topicSlug,
      topicName: ep.topicSlug, // Using slug as name for now
      title: ep.title,
      summary: ep.summary,
      audioS3Key: ep.audioS3Key,
      durationSeconds: ep.durationSeconds,
      publishedAt: ep.publishedAt,
      chapters: ep.chapters.map((ch) => ({
        id: ch.id,
        title: ch.title,
        startTimeMs: ch.startTimeMs,
        endTimeMs: ch.endTimeMs,
      })),
      isCompleted: ep.viewerListenProgress?.completed ?? false,
    }));
  }, []);

  const handlePlayEpisode = useCallback((episode: EpisodeData) => {
    const isCurrentEpisode = currentEpisode?.episodeId === episode.episodeId;
    if (isCurrentEpisode) {
      setIsPlaying(!isPlaying);
    } else {
      // Set the episode queue for autoplay functionality
      setEpisodeQueue(buildEpisodeQueue(episodes));

      playEpisode({
        episodeId: episode.episodeId,
        topicSlug: episode.topicSlug,
        topicName: episode.topicSlug, // Using slug as name for now
        title: episode.title,
        summary: episode.summary,
        audioS3Key: episode.audioS3Key,
        durationSeconds: episode.durationSeconds,
        publishedAt: episode.publishedAt,
        chapters: episode.chapters.map((ch) => ({
          id: ch.id,
          title: ch.title,
          startTimeMs: ch.startTimeMs,
          endTimeMs: ch.endTimeMs,
        })),
      });
    }
  }, [currentEpisode, isPlaying, playEpisode, setIsPlaying, setEpisodeQueue, buildEpisodeQueue, episodes]);

  const handleLike = useCallback((episode: EpisodeData) => {
    setPendingLikeEpisodeId(episode.episodeId);
    commitLike({
      variables: {
        input: {
          topicSlug: episode.topicSlug,
          episodeId: episode.episodeId,
        },
      },
      optimisticResponse: {
        likeEpisode: {
          episode: {
            id: episode.id,
            isLikedByViewer: true,
            likeCount: episode.likeCount + 1,
          },
          userErrors: [],
        },
      },
      onCompleted: () => {
        setPendingLikeEpisodeId(null);
      },
      onError: (error) => {
        console.error('Failed to like episode:', error);
        setPendingLikeEpisodeId(null);
      },
    });
  }, [commitLike]);

  const handleUnlike = useCallback((episode: EpisodeData) => {
    setPendingLikeEpisodeId(episode.episodeId);
    commitUnlike({
      variables: {
        input: {
          topicSlug: episode.topicSlug,
          episodeId: episode.episodeId,
        },
      },
      optimisticResponse: {
        unlikeEpisode: {
          episode: {
            id: episode.id,
            isLikedByViewer: false,
            likeCount: Math.max(0, episode.likeCount - 1),
          },
          userErrors: [],
        },
      },
      onCompleted: () => {
        setPendingLikeEpisodeId(null);
      },
      onError: (error) => {
        console.error('Failed to unlike episode:', error);
        setPendingLikeEpisodeId(null);
      },
    });
  }, [commitUnlike]);

  if (episodes.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Your Feed</h2>
            <p className="mt-1 text-gray-400">
              Latest episodes from topics you follow
            </p>
          </div>

          {/* Autoplay toggle */}
          <button
            type="button"
            onClick={() => setAutoplayEnabled(!autoplayEnabled)}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              autoplayEnabled
                ? 'bg-indigo-600 text-white hover:bg-indigo-500'
                : 'border border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            aria-pressed={autoplayEnabled}
          >
            <AutoplayIcon enabled={autoplayEnabled} className="h-4 w-4" />
            <span className="hidden sm:inline">Autoplay</span>
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {episodes.map((episode) => (
          <EpisodeFeedCard
            key={episode.id}
            episode={episode}
            onPlay={() => handlePlayEpisode(episode)}
            onLike={() => handleLike(episode)}
            onUnlike={() => handleUnlike(episode)}
            isCurrentEpisode={currentEpisode?.episodeId === episode.episodeId}
            isPlaying={currentEpisode?.episodeId === episode.episodeId && isPlaying}
            isLikeMutationPending={pendingLikeEpisodeId === episode.episodeId}
          />
        ))}
      </div>

      {hasNext && (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => loadNext(20)}
            disabled={isLoadingNext}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingNext ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </button>
        </div>
      )}
    </>
  );
}

function DashboardHomeLoader() {
  const data = useLazyLoadQuery<DashboardHomeQuery>(dashboardHomeQuery, {}, {
    fetchPolicy: 'store-and-network',
  });

  return <DashboardHomeContent queryRef={data} />;
}

export function DashboardHome() {
  return (
    <QueryErrorBoundary>
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          </div>
        }
      >
        <DashboardHomeLoader />
      </Suspense>
    </QueryErrorBoundary>
  );
}
